import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Trash2,
  Edit,
  X,
  Save,
  Upload,
  Loader2,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { getApiClient, IS_DEV } from "../../libs/axiosInstance";
import { getSubdomain } from "../../libs/helper";
import { loadAuth } from "../../libs/authMgmt";

const subdomain = IS_DEV ? loadAuth("company")?.subdomain : getSubdomain();
const BASE_API_URL =
  "/api/companies/" + subdomain + "/payments/payment-options";

// --- Sub-Component: Add/Edit Modal (Bootstrap Structure) ---
const AddEditModal = ({
  isOpen,
  onClose,
  onSubmit,
  optionToEdit,
  isLoading,
  isTerminal,
  setIsTerminal
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [error, setError] = useState("");

  const isEditMode = !!optionToEdit;

  useEffect(() => {
    if (optionToEdit) {
      setName(optionToEdit.name || "");
      setDescription(optionToEdit.description || "");
      setCurrentImageUrl(optionToEdit.imageUrl || "");
      setIsTerminal(optionToEdit.isTerminal ?? false)
      setImageFile(null);
    } else {
      setName("");
      setDescription("");
      setCurrentImageUrl("");
      setImageFile(null);
    }
    setError("");
  }, [optionToEdit, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Payment option name is required.");
      return;
    }

    if (!isEditMode && !imageFile && isTerminal === false) {
      setError("An image file is required for a new payment option.");
      return;
    }

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("description", description.trim());
    formData.append("isTerminal", isTerminal);
    if (imageFile) {
      formData.append("image", imageFile);
    }

    if (isEditMode) {
      formData.append("id", optionToEdit._id);
    }

    onSubmit(formData, isEditMode);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        setError("Image file size must not exceed 5MB.");
        setImageFile(null);
        e.target.value = null;
        return;
      }
      setImageFile(file);
      setError("");
    }
  };

  // Render nothing if the modal is not open
  if (!isOpen) return null;

  return (
    // Mimic Bootstrap Modal: fixed backdrop and modal container
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content shadow-lg border-0 rounded-3">
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? "Edit Payment Option" : "Add New Payment Option"}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div
                  className="alert alert-danger d-flex align-items-center"
                  role="alert"
                >
                  <AlertTriangle size={20} className="me-2" />
                  <div>{error}</div>
                </div>
              )}

              <div>
                <label htmlFor="name" className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  disabled={isLoading}
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="form-label">
                  Description (Max 200 chars)
                </label>
                <textarea
                  id="description"
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  maxLength={200}
                  disabled={isLoading}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="image" className="form-label">
                  {isTerminal?"Image File":"QR Image File"} ({isEditMode ? "Optional Update" : isTerminal?"Optional":"Required"})
                </label>
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="form-control"
                  disabled={isLoading}
                />

                {(currentImageUrl || imageFile) && (
                  <div className="mt-3 p-3 bg-light border rounded d-flex align-items-center">
                    <img
                      src={
                        imageFile
                          ? URL.createObjectURL(imageFile)
                          : currentImageUrl
                      }
                      alt="Preview"
                      className="me-3"
                      style={{
                        width: "48px",
                        height: "48px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://placehold.co/48x48/DC3545/ffffff?text=Err";
                      }}
                    />
                    <span className="text-muted">
                      {imageFile ? imageFile.name : `Current Image (URL)`}
                    </span>
                  </div>
                )}
                {!isEditMode && (
                  <small className="form-text text-muted">
                    Maximum size 5MB. JPEG, PNG, or SVG recommended.
                  </small>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2
                      size={18}
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} className="me-2" />{" "}
                    {isEditMode ? "Save Changes" : "Add Option"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
export default function CustomPaymentOptionsManager({ apiUrl = BASE_API_URL }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [globalSuccess, setGlobalSuccess] = useState("");
  const [isTerminal, setIsTerminal] = useState(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [optionToEdit, setOptionToEdit] = useState(null);

  // Confirmation Dialog State
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // --- Data Fetching ---
  const fetchOptions = useCallback(async () => {
    setLoading(true);
    setGlobalError("");
    try {
      const client = await getApiClient();
      const response = await client.get(apiUrl);
      setOptions(response.data || []);
    } catch (err) {
      setGlobalError("Failed to load payment options. Please try refreshing.");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  // Fetch data only once on mount
  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  // --- CRUD Handlers ---

  const handleOpenModal = (option = null) => {
    setOptionToEdit(option);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setOptionToEdit(null);
  };

  const handleModalSubmit = async (formData, isEditMode) => {
    setLoading(true);
    setGlobalError("");
    setGlobalSuccess("");

    try {
      const client = await getApiClient();
      let response;

      if (isEditMode) {
        const id = formData.get("id");
        response = await client.put(`${apiUrl}?id=${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setOptions((prev) =>
          prev.map((opt) =>
            opt._id === id ? { ...opt, ...response.data.option, _id: id } : opt
          )
        );
      } else {
        response = await client.post(apiUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setOptions((prev) => [...prev, response.data.option]);
      }

      setGlobalSuccess(response.data.message || "Operation successful!");
      handleCloseModal();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "A network error occurred during saving.";
      setGlobalError(message);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setGlobalSuccess("");
        setGlobalError("");
      }, 5000);
    }
  };

  const handleConfirmDelete = (option) => {
    setItemToDelete(option);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setLoading(true);
    setShowConfirm(false);
    setGlobalError("");
    setGlobalSuccess("");

    try {
      const client = await getApiClient();
      const response = await client.delete(`${apiUrl}?id=${itemToDelete._id}`);

      setOptions((prev) => prev.filter((opt) => opt._id !== itemToDelete._id));

      setGlobalSuccess(response.data.message || "Payment option deleted.");
    } catch (err) {
      const message =
        err.response?.data?.message || "Failed to delete payment option.";
      setGlobalError(message);
    } finally {
      setLoading(false);
      setItemToDelete(null);
      setTimeout(() => {
        setGlobalSuccess("");
        setGlobalError("");
      }, 5000);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
    setItemToDelete(null);
  };

  // --- Render Functions ---

  const renderStatusMessage = (message, type) => (
    <div
      className={`alert ${
        type === "error" ? "alert-danger" : "alert-success"
      } d-flex align-items-center my-3`}
      role="alert"
    >
      {type === "error" ? (
        <AlertTriangle size={20} className="me-2" />
      ) : (
        <CheckCircle size={20} className="me-2" />
      )}
      <span className="fw-medium">{message}</span>
    </div>
  );

  const renderOptionCard = (option) => (
    // Bootstrap Card structure
    <div key={option._id} className="card shadow-sm border-0 h-100">
      <div className="card-body d-flex align-items-start">
        {/* Thumbnail */}
        <img
          src={option.imageUrl}
          alt={option.name}
          className="rounded-3 border me-3"
          style={{ width: "64px", height: "64px", objectFit: "cover" }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://placehold.co/64x64/6C757D/ffffff?text=N/A";
          }}
        />

        {/* Content */}
        <div className="flex-grow-1">
          <h5 className="card-title mb-1">{option.name}</h5>
          <p className="card-text text-muted small">
            {option.description || "No description provided."}
          </p>
          <small className="text-secondary">
            Added: {new Date(option.createdAt).toLocaleDateString()}
          </small>
        </div>

        {/* Actions - using a dropdown-like vertical layout for simplicity */}
        <div className="d-flex flex-column ms-3">
          <button
            onClick={() => handleOpenModal(option)}
            className="btn btn-outline-primary btn-sm mb-2"
            title="Edit"
            disabled={loading}
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleConfirmDelete(option)}
            className="btn btn-outline-danger btn-sm"
            title="Delete"
            disabled={loading}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  // --- Confirmation Dialog (Bootstrap Modal Structure) ---
  const ConfirmationDialog = () =>
    showConfirm && (
      <div
        className="modal d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      >
        <div className="modal-dialog modal-sm modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-3 text-center">
            <div className="modal-body p-4">
              <AlertTriangle size={48} className="text-danger mx-auto mb-3" />
              <h5 className="modal-title mb-2">Confirm Deletion</h5>
              <p className="text-muted small">
                Are you sure you want to delete{" "}
                <strong>{itemToDelete?.name}</strong>? This action cannot be
                undone.
              </p>
            </div>

            <div className="modal-footer justify-content-center border-top-0">
              <button
                onClick={handleCancelDelete}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger d-flex align-items-center"
                disabled={loading}
              >
                {loading ? (
                  <Loader2
                    size={18}
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <Trash2 size={18} className="me-2" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    // Bootstrap Container for main layout
    <div className="">
      {/* Header and Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <p className="text-secondary">
          Manage custom payment methods for your company.
        </p>
      </div>

      {/* Status Messages */}
      {globalError && renderStatusMessage(globalError, "error")}
      {globalSuccess && renderStatusMessage(globalSuccess, "success")}
      {loading && (
        <div className="text-center p-4 text-primary">
          <Loader2
            size={24}
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />{" "}
          Loading data...
        </div>
      )}

      {/* Payment Options List - Bootstrap Grid | Personal Terminal*/}
      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="">Card Terminal</h5>
          <button
            onClick={() => {
              setIsTerminal(true);
              handleOpenModal(null);
            }}
            className="btn btn-success d-flex align-items-center shadow-sm"
            disabled={loading}
          >
            <Plus size={20} className="me-2" /> Add
          </button>
      </div>
      <hr />
      <div className="row g-4">
        {options.filter(o=>o.isTerminal).length > 0
          ? options.filter(o=>o.isTerminal).map((option) => (
              //col-12 col-md-6 col-lg-4
              <div key={option._id} className="col-12 mb-2">
                {renderOptionCard(option)}
              </div>
            ))
          : !loading && (
              <div className="col-12">
                <div className="p-5 text-center border border-dashed rounded text-muted">
                  No Company Card Terminal payment options found. Click "Add New Option" to get
                  started.
                </div>
              </div>
            )}
      </div>

      {/* Payment Options List - Bootstrap Grid | QR Codes */}
      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="">QR Code Payment</h5>
          <button
            onClick={() => {
              setIsTerminal(false);
              handleOpenModal(null);
            }}
            className="btn btn-success d-flex align-items-center shadow-sm"
            disabled={loading}
          >
            <Plus size={20} className="me-2" /> Add
          </button>
      </div>
      <hr />
      <div className="row g-4">
        {options.filter(o=>!o.isTerminal || o.isTerminal === null).length > 0
          ? options.filter(o=>!o.isTerminal || o.isTerminal === null).map((option) => (
              //col-12 col-md-6 col-lg-4
              <div key={option._id} className="col-12 mb-2">
                {renderOptionCard(option)}
              </div>
            ))
          : !loading && (
              <div className="col-12">
                <div className="p-5 text-center border border-dashed rounded text-muted">
                  No QR payment options found. Click "Add New Option" to get
                  started.
                </div>
              </div>
            )}
      </div>

      {/* Modals and Dialogs */}
      <AddEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        optionToEdit={optionToEdit}
        isLoading={loading}
        isTerminal={isTerminal}
        setIsTerminal={setIsTerminal}
      />

      <ConfirmationDialog />
    </div>
  );
}
